<script setup lang="ts">
import { computed } from 'vue'

import type {
  ExportDestinationType,
  ExportFrequency,
  ExportSummaryData,
} from '../../../models/export.types'
import { resolveDatasetName } from '../../../service/summary'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

const props = defineProps<{ summary: ExportSummaryData }>()
const { t } = useI18n()
const fmt = useFormat()

const DESTINATION_LABEL_KEYS: Record<ExportDestinationType, string> = {
  email: 'dataExport.summary.destination.email',
  google_sheets: 'dataExport.summary.destination.googleSheets',
  big_query: 'dataExport.summary.destination.bigQuery',
}
const FREQUENCY_LABEL_KEYS: Record<ExportFrequency, string> = {
  once: 'dataExport.schedule.frequencies.once',
  daily: 'dataExport.schedule.frequencies.daily',
  weekly: 'dataExport.schedule.frequencies.weekly',
  monthly: 'dataExport.schedule.frequencies.monthly',
}

const dimensions = computed(() => props.summary.columns.filter((column) => !column.numeric))
const metrics = computed(() => props.summary.columns.filter((column) => column.numeric))
const datasetName = computed(() => resolveDatasetName(props.summary.dataset))
const destinationLabel = computed(() => t(DESTINATION_LABEL_KEYS[props.summary.destination]))
const frequencyLabel = computed(() => t(FREQUENCY_LABEL_KEYS[props.summary.frequency]))
</script>

<template>
  <section class="export-summary" :aria-label="t('dataExport.summary.title')">
    <dl class="export-summary__list grid gap-sm m-0 p-md border border-border rounded-md">
      <div>
        <dt>{{ t('dataExport.summary.name') }}</dt>
        <dd>{{ summary.name }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.platforms') }}</dt>
        <dd>{{ fmt.formatList(summary.platforms) }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.dimensions') }}</dt>
        <dd>{{ fmt.formatList(dimensions.map((column) => column.header)) }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.metrics') }}</dt>
        <dd>{{ fmt.formatList(metrics.map((column) => column.header)) }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.filters') }}</dt>
        <dd>{{ t('dataExport.summary.filterCount', { count: summary.filterCount }) }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.dateRange') }}</dt>
        <dd>{{ fmt.formatDateRange(summary.firstDate, summary.lastDate) }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.destinationLabel') }}</dt>
        <dd>{{ destinationLabel }}</dd>
      </div>
      <div v-if="summary.destination !== 'email'">
        <dt>{{ t('dataExport.summary.writeMode') }}</dt>
        <dd>{{ summary.updateMode }}</dd>
      </div>
      <div>
        <dt>{{ t('dataExport.summary.schedule') }}</dt>
        <dd>{{ frequencyLabel }}</dd>
      </div>
      <div v-if="summary.destination !== 'big_query'">
        <dt>{{ t('dataExport.summary.recipients') }}</dt>
        <dd>{{ fmt.formatList(summary.recipients ?? []) }}</dd>
      </div>
      <template v-else>
        <div>
          <dt>{{ t('dataExport.summary.project') }}</dt>
          <dd>{{ summary.projectName }}</dd>
        </div>
        <div>
          <dt>{{ t('dataExport.summary.dataset') }}</dt>
          <dd>{{ datasetName }}</dd>
        </div>
      </template>
    </dl>
  </section>
</template>

<style scoped>
.export-summary .export-summary__list > div {
  display: grid;
  grid-template-columns: minmax(8rem, 0.35fr) 1fr;
  gap: var(--ui-space-md);
}
.export-summary dt {
  color: var(--ui-color-subtle);
  font-weight: 600;
}
.export-summary dd {
  margin: 0;
}
</style>
